/**
 * <p-loop-slider> — schlanker Endlos-Slider (Ersatz für Hypers Swiper in
 * social-proof-reviews + variant-product-slider). Keine Library.
 *
 * Layout kommt komplett aus dem CSS der Section (Kartenbreite, Abstand, Zentrierung je Breakpoint):
 *   [data-slider-viewport]  overflow: hidden
 *   [data-slider-track]     display: flex; column-gap = Kartenabstand
 *   Kinder des Tracks       = Slides (feste Breite per CSS)
 *   CSS-Variable --slider-center: 1 → aktive Karte mittig (wie Swiper centeredSlides)
 * Optional: [data-slider-prev] / [data-slider-next], [data-slider-dots] (Punkte je Slide).
 * data-dot-label="Gehe zu Folie [n]" → aria-label der Punkte.
 * Verhalten wie Hyper: Loop, 500 ms, Wisch-Schwelle 8 px, Maus-Drag, Klick nach Drag wird unterdrückt.
 */
if (!customElements.get('p-loop-slider')) {
  const SPEED = 500;
  const THRESHOLD = 8;

  class PLoopSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      if (!this.viewport || !this.track || this.slides) return;

      this.slides = [...this.track.children];
      this.count = this.slides.length;
      this.index = 0;
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.dotsEl = this.querySelector('[data-slider-dots]');

      this.slides.forEach((slide, i) => (slide.dataset.sliderIndex = i));
      this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));
      this.buildDots();
      this.bindDrag();

      this.track.addEventListener('transitionend', (e) => {
        if (e.target === this.track) this.normalize();
      });
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.viewport);

      if (window.Shopify?.designMode) {
        this.onBlockSelect = (e) => {
          const slide = e.target.closest?.('[data-slider-index]');
          if (slide && this.contains(slide)) this.go(Number(slide.dataset.sliderIndex));
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
      this.layout();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* Maße aus dem CSS lesen, Klone für die Endlosschleife anlegen */
    layout() {
      const first = this.slides[0];
      if (!first) return;
      const width = this.viewport.clientWidth;
      const slideWidth = first.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.step = slideWidth + gap;
      this.center = getComputedStyle(this).getPropertyValue('--slider-center').trim() === '1';
      this.offset = this.center ? (width - slideWidth) / 2 : 0;

      const perView = (width + gap) / this.step;
      this.enabled = this.count > perView + 0.01;
      this.toggleAttribute('data-locked', !this.enabled);

      const clones = this.enabled ? Math.ceil(perView) + 1 : 0;
      if (clones !== this.clones) this.buildClones(clones);
      if (!this.enabled) this.index = 0;
      this.move(false);
    }

    buildClones(amount) {
      this.track.querySelectorAll('[data-slider-clone]').forEach((c) => c.remove());
      this.clones = amount;
      const make = (slide) => {
        const clone = slide.cloneNode(true);
        clone.dataset.sliderClone = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        return clone;
      };
      for (let i = 0; i < amount; i++) {
        this.track.prepend(make(this.slides[(this.count - 1 - (i % this.count) + this.count) % this.count]));
        this.track.append(make(this.slides[i % this.count]));
      }
    }

    position(index) {
      return -((index + this.clones) * this.step) + this.offset;
    }

    move(animate, extra = 0) {
      this.track.style.transition = animate ? `transform ${SPEED}ms ease` : 'none';
      this.track.style.transform = `translate3d(${this.position(this.index) + extra}px, 0, 0)`;
      this.updateDots();
    }

    go(index) {
      if (!this.enabled) return;
      // Während eine Schleifen-Animation läuft, erst normalisieren
      if (this.index < 0 || this.index >= this.count) this.normalize();
      this.index = Math.max(-this.clones, Math.min(this.count - 1 + this.clones, index));
      this.move(true);
    }

    normalize() {
      if (this.index < 0 || this.index >= this.count) {
        this.index = ((this.index % this.count) + this.count) % this.count;
        this.move(false);
      }
    }

    buildDots() {
      if (!this.dotsEl) return;
      const label = this.dataset.dotLabel || '';
      this.dotsEl.innerHTML = '';
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'p-loop-slider__dot';
        dot.setAttribute('aria-label', label.replace('[n]', i + 1));
        dot.addEventListener('click', () => this.go(i));
        this.dotsEl.appendChild(dot);
        return dot;
      });
    }

    updateDots() {
      if (!this.dots) return;
      const active = ((this.index % this.count) + this.count) % this.count;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
        dot.toggleAttribute('aria-current', i === active);
      });
    }

    bindDrag() {
      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let dx = 0;
      let pointerId = null;
      let dragging = false;
      let decided = false;

      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());

      this.viewport.addEventListener('pointerdown', (e) => {
        if (!this.enabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
        this.normalize();
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        startTime = Date.now();
        dx = 0;
        dragging = false;
        decided = false;
      });

      this.viewport.addEventListener('pointermove', (e) => {
        if (e.pointerId !== pointerId) return;
        const moveX = e.clientX - startX;
        const moveY = e.clientY - startY;
        if (!decided) {
          if (Math.abs(moveX) < THRESHOLD && Math.abs(moveY) < THRESHOLD) return;
          decided = true;
          dragging = Math.abs(moveX) > Math.abs(moveY);
          if (!dragging) {
            pointerId = null;
            return;
          }
          this.viewport.setPointerCapture?.(e.pointerId);
          this.suppressClick = true;
        }
        dx = moveX - Math.sign(moveX) * THRESHOLD;
        this.move(false, dx);
      });

      const end = (e) => {
        if (e.pointerId !== pointerId) return;
        pointerId = null;
        if (!dragging) return;
        dragging = false;
        const moved = -dx / this.step;
        const quick = Date.now() - startTime < 300;
        let steps = Math.round(moved);
        if (quick && steps === 0 && Math.abs(dx) > 0) steps = moved > 0 ? 1 : -1;
        this.index = Math.max(-this.clones, Math.min(this.count - 1 + this.clones, this.index + steps));
        this.move(true);
        setTimeout(() => (this.suppressClick = false), 50);
      };
      this.viewport.addEventListener('pointerup', end);
      this.viewport.addEventListener('pointercancel', end);

      this.viewport.addEventListener(
        'click',
        (e) => {
          if (this.suppressClick) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    }
  }

  customElements.define('p-loop-slider', PLoopSlider);
}
